export interface LottoWebDataRelease {
  readonly releaseId: string;
  readonly dataVersion: number;
  readonly sourceAsOf: string;
  readonly basePath: string;
  readonly storesPath: "stores.json";
  readonly drawsPath: "draws.json";
  readonly winningHistoryPath: "winning-history.json" | null;
  readonly winningStorePaths: ReadonlyMap<number, string>;
}

interface Pointer { readonly schemaVersion: 1; readonly releaseId: string; readonly dataVersion: number }
interface ReleaseManifest {
  readonly schemaVersion: 1;
  readonly releaseId: string;
  readonly dataVersion: number;
  readonly sourceAsOf: string;
  readonly artifacts: {
    readonly stores: { readonly path: "stores.json" };
    readonly draws: { readonly path: "draws.json" };
    readonly winningHistory?: { readonly path: "winning-history.json" };
    readonly drawStores: readonly { readonly drawNo: number; readonly path: string }[];
  };
}

/** Reads only an immutable public data release selected by a small current pointer. */
export function loadLottoWebDataRelease(): Promise<LottoWebDataRelease | null> {
  return loadRelease();
}

export async function loadLottoReleaseJson<T>(release: LottoWebDataRelease, path: string): Promise<T> {
  if (!isArtifactPath(path)) throw new Error("invalid_lotto_web_artifact_path");
  const response = await fetch(`${release.basePath}/${path}`, { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error("lotto_web_artifact_unavailable");
  return response.json() as Promise<T>;
}

function isArtifactPath(value: string): boolean {
  return value === "stores.json" || value === "draws.json" || value === "winning-history.json" || /^draw-stores\/[1-9][0-9]*\.json$/.test(value);
}

async function loadRelease(): Promise<LottoWebDataRelease | null> {
  const pointerResponse = await fetch("/data/current.json", { headers: { Accept: "application/json", "Cache-Control": "no-store" } });
  if (pointerResponse.status === 404) return null;
  if (!pointerResponse.ok) throw new Error("web_data_pointer_unavailable");
  const pointer: unknown = await pointerResponse.json();
  if (!isPointer(pointer)) throw new Error("invalid_web_data_pointer");
  const basePath = `/data/releases/${encodeURIComponent(pointer.releaseId)}`;
  const releaseResponse = await fetch(`${basePath}/release.json`, { headers: { Accept: "application/json", "Cache-Control": "no-store" } });
  if (!releaseResponse.ok) throw new Error("web_data_release_unavailable");
  const release: unknown = await releaseResponse.json();
  if (!isReleaseManifest(release) || release.releaseId !== pointer.releaseId || release.dataVersion !== pointer.dataVersion) throw new Error("invalid_web_data_release");
  const winningStorePaths = new Map<number, string>();
  for (const artifact of release.artifacts.drawStores) {
    if (winningStorePaths.has(artifact.drawNo)) throw new Error("duplicate_draw_store_artifact");
    winningStorePaths.set(artifact.drawNo, artifact.path);
  }
  return {
    releaseId: release.releaseId,
    dataVersion: release.dataVersion,
    sourceAsOf: release.sourceAsOf,
    basePath,
    storesPath: release.artifacts.stores.path,
    drawsPath: release.artifacts.draws.path,
    winningHistoryPath: release.artifacts.winningHistory?.path ?? null,
    winningStorePaths,
  };
}

function isPointer(value: unknown): value is Pointer {
  return isRecord(value) && value.schemaVersion === 1 && typeof value.releaseId === "string"
    && /^lotto-web-v[1-9][0-9]*-[0-9a-f]{12}$/.test(value.releaseId)
    && Number.isInteger(value.dataVersion) && Number(value.dataVersion) > 0;
}

function isReleaseManifest(value: unknown): value is ReleaseManifest {
  if (!isRecord(value) || value.schemaVersion !== 1 || typeof value.releaseId !== "string" || !Number.isInteger(value.dataVersion)
    || typeof value.sourceAsOf !== "string" || !isRecord(value.artifacts) || !isRecord(value.artifacts.stores) || !isRecord(value.artifacts.draws)
    || !Array.isArray(value.artifacts.drawStores) || value.artifacts.stores.path !== "stores.json" || value.artifacts.draws.path !== "draws.json") return false;
  if (value.artifacts.winningHistory !== undefined && (!isRecord(value.artifacts.winningHistory) || value.artifacts.winningHistory.path !== "winning-history.json")) return false;
  return value.artifacts.drawStores.every((item) => isRecord(item) && Number.isInteger(item.drawNo) && Number(item.drawNo) > 0 && typeof item.path === "string" && /^draw-stores\/[1-9][0-9]*\.json$/.test(item.path));
}

function isRecord(value: unknown): value is Record<string, unknown> { return typeof value === "object" && value !== null; }
